<template>
  <div v-if="item.filler" class="item filler" />
  <div
    v-else
    class="item"
    :style="{'--color': color}"
    :class="{selected: item.selected, blacklisted: item.blacklisted, disabled: item.disabled}"
    @click="onClick"
  >
    <v-img
      :transition="false"
      :aspect-ratio="1"
      :src="`https://community.cloudflare.steamstatic.com/economy/image/${item.image}/164x164`"
      contain
    />
    <div class="inner">
      <!-- item attributes -->
      <div class="price lh-1 d-flex flex-center-y">
        <span v-if="dollarValue" class="font-weight-bold">${{ item.price || 0 | toScrap }}</span>
        <scrap v-else :amount="item.price || 0" />

        <span
          v-show="item.selected && item.amountSelected > 1"
          class="ml-2 font-weight-bold lh-1"
        >
          (x{{ amountSelected }})
        </span>
      </div>

      <span v-if="item.discount" class="discount green700 px-1 text-caption green200--text ml-2">
        -{{ item.discount * 100 | toLocale(2) }}%
      </span>

      <span
        v-if="item.discount"
        class="old-price"
      >
        {{ (item.price || 0) * (1 + item.discount) | toScrap }}
      </span>

      <div v-show="item.amount > 1" class="item-amount grey100--text lh-1">
        x{{ item.amount }}
      </div>

      <!-- hover overlay -->
      <div class="meta">
        <div class="item-info">
          <div
            v-if="item.blacklisted"
            v-t="'common.items.blacklisted'"
            class="primary500--text font-weight-bold text-caption text-uppercase mb-2"
          />

          <div
            :style="{'color': color}"
            class="name mb-1 font-weight-bold"
          >
            {{ item.name | truncate(50) }}
          </div>

          <a
            v-if="item.blacklisted"
            href="https://banditcamp.tawk.help/article/why-is-my-inventory-underpriced"
            target="_blank" rel="noopener noreferrer"
            class="link d-inline-block blue200--text"
          >
            <fai :icon="['fas', 'question-circle']" class="mr-1" />
            <span v-t="'common.why'" />
          </a>
        </div>
        <!-- item amount slider -->
        <div v-if="item.amount > 1 && !item.blacklisted" class="amount-slider text-left lh-1">
          <v-slider
            v-model="amountSelected"
            class="slider-ctn"
            :max="item.amount"
            :min="1"
            height="30"
            step="1"
            thumb-label="always"
            hide-details
            @click.stop
            @end="$emit('onItemAmountChange', item, $event)"
          >
            <template #thumb-label="{value}">
              <div class="font-weight-bold px-2 caption text-uppercase grey100--text">
                {{ $t('common.amount') }}: {{ value }}
              </div>
            </template>
          </v-slider>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    data: {
      type: Object,
      required: true,
    },
    dollarValue: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      amountSelected: this.data.amountSelected,
    }
  },
  computed: {
    item() {
      return this.data
    },
    color() {
      const { item } = this
      return item.colors?.name ?? item.color
    },
  },
  watch: {
    'data.amountSelected': function onAmountChanged(amount) {
      this.amountSelected = amount
    },
  },
  methods: {
    onClick() {
      this.$emit('onClick', this.item)
    },
  },
}
</script>

<style lang="scss" scoped>
$panelbg: rgba(grey(300), 0.75);

.item {
  position: relative;
  background-color: #413f3d;
  cursor: pointer;
  flex: 1;

  &::v-deep .v-image {
    position: relative;
    z-index: 2;
    margin: 10px;
  }

  .inner {
    overflow: hidden;
    position: absolute;
    z-index: 3;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    .price {
      position: absolute;
      top: 12px;
      left: 8px;
      text-shadow: 0 0 8px black;
      font-size: 14px;
    }

    .old-price {
      position: absolute;
      z-index: 2;
      top: 24px;
      left: 30px;
      color: primary(300);
      text-shadow: 0 0 4px black;
      font-size: 13px;
      text-decoration: line-through;
    }

    .discount {
      position: absolute;
      top: 8px;
      right: 10px;
    }

    .item-amount {
      position: absolute;
      right: 0;
      bottom: 0;
      padding: 16px 14px;
      color: $text;
      text-shadow: 0 0 8px black;
      font-size: 22px;
    }
  }

  .meta {
    display: flex;
    flex-direction: column-reverse;
    position: absolute;
    z-index: 5;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 8px;
    opacity: 0;
    background: rgba(grey(900), 0.8);
    transition: opacity 0.3s ease;
    justify-content: center;
    will-change: opacity;

    .item-info {
      width: 100%;
      opacity: 0;
      color: white;
      // font-family: $family-primary;
      font-size: 14px;
      font-weight: 600;
      text-align: center;
      user-select: none;
      transition: transform 0.3s ease, opacity 0.3s ease;
      transform: translateY(40px);

      .attr-small {
        font-size: 11px;
      }
    }

    .amount-slider {
      position: absolute;
      bottom: 0;
      left: 0;
      width: 100%;
    }

    &:hover {
      opacity: 1;

      .item-info {
        opacity: 1;
        transform: translateY(0);
      }
    }
  }

  &.filler {
    // visibility: hidden;
    background-color: rgba($panelbg, 0.7);
    cursor: default;
  }

  &.disabled {
    cursor: no-drop;

    ::v-deep .v-image,
    .inner {
      opacity: 0.4;
    }

    &::before {
      background: radial-gradient(grey(900), transparent);
    }

    .meta {
      display: none;
    }
  }

  &.blacklisted {
    cursor: no-drop;

    &::before {
      background: radial-gradient(white, transparent);
    }

    .meta {
      opacity: 1;

      .item-info {
        opacity: 1;
        transform: translateY(0);

        .name {
          color: grey(100) !important;
          font-size: 12px;
        }
      }
    }

    .v-image {
      filter: grayscale(1);
    }
  }

  &.selected {
    background-color: darken(blue(), 7%);

    &::before,
    &::after {
      display: none;
    }
  }

  &:first-child {
    margin-left: 0;
  }

  &:last-child {
    margin-right: 0;
  }

  &::before {
    position: absolute;
    z-index: 1;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.35;
    background: radial-gradient(var(--color), transparent);
    content: ' ';
  }

  // &::after {
  //   position: absolute;
  //   z-index: 1;
  //   bottom: 0;
  //   left: 0;
  //   width: 100%;
  //   height: 2px;
  //   background: var(--color);
  //   content: ' ';
  // }
}

.slider-ctn ::v-deep {
  .v-slider {
    height: 100%;
    min-height: unset;
    margin: 0;

    .v-slider__track-container {
      height: 100%;

      .v-slider__track-background {
        background-color: $panelbg !important;
      }

      .v-slider__track-fill {
        background-color: rgba(grey(100), 0.7) !important;
      }
    }

    .v-slider__thumb-container {
      position: absolute;
      top: 0;
      left: 0 !important;
      width: 100%;
      height: 100%;

      .v-slider__thumb {
        display: none;
      }

      .v-slider__thumb-label-container {
        width: 100%;
        height: 100%;
        // overflow: hidden;

        .v-slider__thumb-label {
          position: absolute;
          top: 0;
          left: 0;
          width: 100% !important;
          height: 100% !important;
          padding: 0;
          background: none !important;
          transform: none !important;
          justify-content: flex-start;

          > * {
            transform: none !important;
          }
        }
      }

      // display: none;
    }
  }
}
</style>
